import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, User, Phone, Mail, Calendar, Star, X, Check, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AVAILABILITY_OPTIONS = [
  { id: 'full_time', name: 'Full Time', color: 'bg-green-100 text-green-700' },
  { id: 'part_time', name: 'Part Time', color: 'bg-blue-100 text-blue-700' },
  { id: 'on_call', name: 'On Call', color: 'bg-amber-100 text-amber-700' },
  { id: 'unavailable', name: 'Unavailable', color: 'bg-neutral-100 text-neutral-500' },
];

const SKILL_OPTIONS = [
  'Roller Shades',
  'Motorized Shades',
  'Roman Shades',
  'Drapes',
  'Motorized Drapes',
  'Skylight Shades',
  'Smart Home Integration',
  'Lutron',
  'Somfy',
  'Repairs',
];

const emptyInstaller = {
  name: '',
  email: '',
  phone: '',
  availability: 'full_time',
  skills: [],
  hourly_rate: '',
  notes: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
};

export const InstallersTab = ({ token }) => {
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInstaller, setEditingInstaller] = useState(null);
  const [formData, setFormData] = useState(emptyInstaller);

  useEffect(() => {
    fetchInstallers();
  }, []);

  const fetchInstallers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/installers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstallers(response.data);
    } catch (error) {
      console.error('Failed to fetch installers:', error);
      toast.error('Failed to load installers');
    } finally {
      setLoading(false);
    }
  };

  const saveInstaller = async () => {
    if (!formData.name.trim()) {
      toast.error('Installer name is required');
      return;
    }

    try {
      if (editingInstaller) {
        await axios.put(`${API_URL}/admin/installers/${editingInstaller.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Installer updated');
      } else {
        await axios.post(`${API_URL}/admin/installers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Installer added');
      }
      setShowModal(false);
      setEditingInstaller(null);
      setFormData(emptyInstaller);
      fetchInstallers();
    } catch (error) {
      toast.error('Failed to save installer');
    }
  };

  const deleteInstaller = async (installerId) => {
    if (!window.confirm('Are you sure you want to delete this installer?')) return;
    try {
      await axios.delete(`${API_URL}/admin/installers/${installerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Installer deleted');
      fetchInstallers();
    } catch (error) {
      toast.error('Failed to delete installer');
    }
  };

  const openCreateModal = () => {
    setEditingInstaller(null);
    setFormData(emptyInstaller);
    setShowModal(true);
  };

  const openEditModal = (installer) => {
    setEditingInstaller(installer);
    setFormData({
      name: installer.name || '',
      email: installer.email || '',
      phone: installer.phone || '',
      availability: installer.availability || 'full_time',
      skills: installer.skills || [],
      hourly_rate: installer.hourly_rate || '',
      notes: installer.notes || '',
      address: installer.address || '',
      city: installer.city || '',
      state: installer.state || '',
      zip_code: installer.zip_code || '',
    });
    setShowModal(true);
  };

  const toggleSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const getAvailabilityInfo = (availabilityId) => {
    return AVAILABILITY_OPTIONS.find(a => a.id === availabilityId) || AVAILABILITY_OPTIONS[0];
  };

  const filteredInstallers = installers.filter(i =>
    i.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.phone?.includes(searchTerm)
  );

  return (
    <div data-testid="installers-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Installers</h2>
          <p className="text-neutral-500 text-sm">Manage your installation team</p>
        </div>
        <Button onClick={openCreateModal} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> Add Installer
        </Button>
      </div>

      {/* Search */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search installers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      ) : filteredInstallers.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg">
          <User className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
          <p className="text-neutral-600">No installers found</p>
          <p className="text-sm text-neutral-500 mt-1">Add your first installer to get started</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredInstallers.map(installer => {
            const availability = getAvailabilityInfo(installer.availability);
            return (
              <div
                key={installer.id}
                className="bg-white border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <h4 className="font-medium">{installer.name}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded ${availability.color}`}>
                        {availability.name}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEditModal(installer)}
                      className="p-1.5 hover:bg-neutral-100 rounded"
                    >
                      <Edit className="w-4 h-4 text-neutral-500" />
                    </button>
                    <button
                      onClick={() => deleteInstaller(installer.id)}
                      className="p-1.5 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {installer.email && (
                    <p className="text-neutral-600 flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-neutral-400" />
                      {installer.email}
                    </p>
                  )}
                  {installer.phone && (
                    <p className="text-neutral-600 flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 text-neutral-400" />
                      {installer.phone}
                    </p>
                  )}
                  {installer.hourly_rate && (
                    <p className="text-neutral-600 flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-neutral-400" />
                      ${installer.hourly_rate}/hr
                    </p>
                  )}
                </div>

                {installer.skills && installer.skills.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-neutral-100">
                    <div className="flex flex-wrap gap-1">
                      {installer.skills.slice(0, 4).map(skill => (
                        <span key={skill} className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded">
                          {skill}
                        </span>
                      ))}
                      {installer.skills.length > 4 && (
                        <span className="text-xs text-neutral-400">
                          +{installer.skills.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Installer Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingInstaller ? 'Edit Installer' : 'Add Installer'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Availability</Label>
                <select
                  value={formData.availability}
                  onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                >
                  {AVAILABILITY_OPTIONS.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Hourly Rate ($)</Label>
                <Input
                  type="number"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  placeholder="50"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Street address"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="City"
              />
              <Input
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="State"
              />
              <Input
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                placeholder="ZIP"
              />
            </div>

            <div>
              <Label>Skills & Specializations</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {SKILL_OPTIONS.map(skill => (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => toggleSkill(skill)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      formData.skills.includes(skill)
                        ? 'bg-amber-100 border-amber-300 text-amber-700'
                        : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300'
                    }`}
                  >
                    {formData.skills.includes(skill) && <Check className="w-3 h-3 inline mr-1" />}
                    {skill}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this installer..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={saveInstaller} className="btn-primary">
              {editingInstaller ? 'Update' : 'Add'} Installer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InstallersTab;
